"use client";

import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import {
  ArrowLeft,
  CheckCircle2,
  ShoppingCart,
  TicketPercent,
  Trash2
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { QuantityStepper } from "@/components/QuantityStepper";
import { formatPrice } from "@/lib/products";
import { saveLocalOrder } from "@/lib/orders";
import { useCart } from "@/lib/cart";
import { StoreImage } from "@/components/StoreImage";
import {
  cartDpTotal,
  cartDpTotalWithExtraLines,
  cartLandingSubTab,
  cartLandingSubTabWithBundles,
  cartSubtotal,
  type CartState
} from "@/lib/cartMulti";
import {
  cartLineRemaining,
  overQuotaCartLines,
  type QuotaByVariant,
} from "@/lib/poQuota";
import { fetchPreorderQuotaRows } from "@/lib/supabase/public-data";
import {
  createCheckoutOrder,
  createCheckoutOrders,
  createCheckoutOrdersWithBundles,
  createCheckoutOrdersWithVoucher,
  friendlyOrderItemInsertError,
  resolveBundlesPricing,
  type BundleCheckoutResult,
  type ResolvedBundle
} from "@/lib/supabase/data";
import {
  CartVoucherSection,
  CartVoucherSummaryCard,
  type CartVoucherSelection,
  type CartVoucherSummary
} from "@/components/cart/CartVoucherSection";
import { BundleCartCard } from "@/components/cart/BundleCartCard";
import { GiftCartCard } from "@/components/cart/GiftCartCard";
import { useBundleCartPricing } from "@/components/cart/useBundleCartPricing";
import { useCartGifts } from "@/components/cart/useCartGifts";
import { attachMyCheckoutGifts } from "@/lib/supabase/gifts";
import { clearCartVoucherPick } from "@/lib/cartVoucherSelection";
import { voucherErrorMessage } from "@/lib/voucher";
import { bundleCustomerSlot, bundleErrorMessage } from "@/lib/bundle";
import { giftErrorMessage } from "@/lib/gift";
import { giftOnlyProductIds } from "@/lib/giftCart";
import { giftEntryHosts, giftLineKeyOf } from "@/lib/giftNesting";
import {
  bundleCartProductIds,
  bundleCheckoutKeyReleasable,
  bundleCheckoutPixelCarts,
  bundleExpectedTotals,
  bundleFinalUnitPrices,
  bundleLinesByProduct,
  bundlesAsCartLines,
  bundlesKnownNotApplying,
  cartBundlesTotal,
  cartGrandTotal,
  cartsAsPixelCarts,
  pixelCartSubtotal,
  type BundleLiveState
} from "@/lib/bundleCheckout";
import { partialCheckoutMessage } from "@/lib/checkoutGuard";
import { buildCheckoutPayload, buildPurchasePayload } from "@/lib/pixel";
import { firePixelEvent } from "@/lib/pixelClient";
import { recordOrderAttribution } from "@/lib/supabase/storeAttribution";
import { useCustomer } from "@/context/CustomerContext";

type CartCheckoutProps = {
  mode?: "page" | "inline";
  /**
   * Apakah produk keranjang ini masih boleh dipesan (status produk live).
   * False = PO sudah ditutup → blokir checkout keranjang basi. Default true.
   */
  canOrder?: boolean;
};

export function CartCheckout({
  mode = "page",
  canOrder = true
}: CartCheckoutProps) {
  const {
    carts,
    cart,
    totalQty,
    subtotal,
    updateLine,
    updateLineNote,
    removeLine,
    clearProduct,
    clearCart,
    replaceCarts,
    bundles,
    updateBundleQty,
    removeBundle,
    clearBundles
  } = useCart();
  const { customer, isLoading: customerLoading, isAuthenticated } = useCustomer();
  const router = useRouter();
  const pathname = usePathname();
  // Hanya pembeli (reseller) yang sudah login boleh membuat pesanan.
  // Bedakan: tamu (belum login) → suruh login; sudah login tapi bukan reseller
  // (mis. admin) → bukan "logout", tapi memang tidak bisa memesan sebagai reseller.
  const needsLogin = !customerLoading && !customer && !isAuthenticated;
  const notReseller = !customerLoading && !customer && isAuthenticated;
  const loginHref = `/login?next=${encodeURIComponent(pathname)}`;

  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Kunci sinkron (bughunt H1-1): `isSubmitting` (state) baru mengunci tombol
  // setelah re-render — jendela balap tipis untuk tap-ganda/Enter-ganda cepat
  // di HP lambat. Ref dicek-lalu-diset SEBELUM apa pun lain, termasuk sebelum
  // `setIsSubmitting`, supaya invocation kedua yang genuinely serentak berhenti
  // di baris pertama tanpa menunggu render.
  const submitLockRef = useRef(false);

  // ─── VOUCHER PRODUK (mig 5610) ────────────────────────────────────────────
  // Pilihan & ringkasan datang dari blok voucher (CartVoucherSection); checkout
  // ber-voucher memakai RPC satu-transaksi, checkout biasa tetap jalur lama.
  const [voucherSelection, setVoucherSelection] = useState<CartVoucherSelection | null>(null);
  const [voucherSummary, setVoucherSummary] = useState<CartVoucherSummary | null>(null);
  // Kunci idempoten per PERCOBAAN: dibuat saat submit pertama, dipertahankan
  // selama gagal (jaringan) supaya kirim-ulang memutar hasil yang sama, dan
  // dibuang setelah sukses.
  const voucherRequestIdRef = useRef<string | null>(null);
  const handleVoucherSelection = useCallback((sel: CartVoucherSelection | null) => {
    setVoucherSelection(sel);
  }, []);
  const handleVoucherSummary = useCallback((sum: CartVoucherSummary | null) => {
    setVoucherSummary(sum);
  }, []);
  // Voucher terpilih yang angkanya belum pasti (pratinjau jalan / harga
  // keranjang belum atau gagal termuat) — tombol "Keep Produk" ditahan
  // supaya pesanan tak dibuat dengan angka yang belum divalidasi server, dan
  // tidak pula diam-diam dibuat TANPA voucher yang sudah dipilih pembeli.
  const [voucherPending, setVoucherPending] = useState(false);
  const handleVoucherPending = useCallback((pending: boolean) => {
    setVoucherPending(pending);
  }, []);
  // Generasi blok voucher: dinaikkan saat checkout ditolak `VOUCHER_INVALID`
  // supaya blok dibangun ulang — pilihan dibaca ulang lalu dipratinjau server
  // lagi, dan voucher yang gugur ikut DILEPAS di layar (dengan kalimat
  // sebabnya). Tanpa ini blok tetap "1 dipakai · Hemat Rp X" sementara
  // ketukan berikutnya diam-diam memesan harga penuh (bughunt D10-1).
  const [voucherSectionKey, setVoucherSectionKey] = useState(0);
  // Produk yang tercakup voucher (dijawab server lewat blok voucher) →
  // keterangan "Terdapat voucher untuk produk ini" di kartu produk (mig 5660).
  const [voucherCovered, setVoucherCovered] = useState<ReadonlySet<string>>(() => new Set());
  const handleVoucherCoverage = useCallback((ids: ReadonlySet<string>) => {
    setVoucherCovered(ids);
  }, []);

  // ─── BUNDEL (mig 8040) ────────────────────────────────────────────────────
  // Bundel tampil sebagai kartu kelompok (`BundleCartCard`) dan, begitu ada,
  // checkout SELURUH keranjang lewat RPC satu transaksi
  // `checkout_orders_with_bundles` (voucher boleh ikut — K5). Keranjang tanpa
  // bundel tak menyentuh satu baris pun di bawah ini.
  const hasBundles = bundles.length > 0;
  // Harga TERKINI bundel untuk pembeli login (resolver checkout): kartu,
  // total, gerbang checkout, dan pratinjau voucher membacanya.
  const bundlePricing = useBundleCartPricing(
    bundles,
    customer?.id ?? null,
    customer?.group?.slot ?? null
  );
  const bundleProductIds = useMemo(() => bundleCartProductIds(bundles), [bundles]);
  // Bundel yang tak tersedia (berakhir, tak berhak, isi berubah) menahan
  // checkout sampai dihapus — tak pernah turun diam-diam ke harga satuan.
  const bundleBlocked = hasBundles && bundlePricing.hasError;
  // Harga terkini bundel untuk isi keranjang SAAT INI belum termuat (pembeli
  // login) — tombol ditahan, idiom `voucherPending`: harga yang dikonfirmasi
  // checkout (§6.4, `expected`) wajib angka yang SEDANG dipajang kartu.
  const bundlePending = Boolean(customer) && hasBundles && !bundlePricing.ready;
  const bundleDpLines = useMemo(
    () => (hasBundles ? bundleLinesByProduct(bundles, bundlePricing.lastByKey) : null),
    [hasBundles, bundles, bundlePricing.lastByKey]
  );
  const bundlesTotal = hasBundles ? cartBundlesTotal(bundles, bundlePricing.lastByKey) : null;
  // Kunci idempoten checkout ber-bundel — idiom `voucherRequestIdRef`.
  const bundleRequestIdRef = useRef<string | null>(null);

  // ─── HADIAH PEMBELIAN (mig 8680) ──────────────────────────────────────────
  // Kartu "Hadiah untukmu" dihitung dari baris biasa keranjang. Keranjang yang
  // membawa hadiah checkout lewat RPC yang sama dengan bundel (`p_gifts`);
  // server menghitung ulang haknya. Hadiah yang gugur di server tak pernah
  // turun diam-diam: pembeli memilih "Lanjut tanpa hadiah" (H14).
  const cartGifts = useCartGifts({
    carts,
    slot: bundleCustomerSlot(customer?.group ?? null),
    customerId: customer?.id ?? null,
    enabled: canOrder
  });
  const [giftProblem, setGiftProblem] = useState<{ text: string; ruleIds: string[] } | null>(null);

  const isInline = mode === "inline";

  // ─── KUOTA PO di keranjang (mig 3970, dilengkapi audit 2026-08-24) ─────────
  // Sampai audit ini keranjang buta kuota: qty disusun di localStorage bisa jauh
  // lebih tua dari kuota varian, dan pembeli baru ditolak SETELAH menekan
  // CHECKOUT — penolakan yang datang dari trigger DB, sesudah alamat & catatan
  // diisi. Di sini sisanya ditanyakan lebih awal supaya angkanya bisa dibatasi
  // di tempat qty diketik.
  //
  // Hanya produk ber-toggle yang menembak RPC; PO biasa (mayoritas) tetap nol
  // round-trip — alasan yang sama dengan `ProductDetail`.
  const [quotaByVariant, setQuotaByVariant] = useState<QuotaByVariant>(
    () => new Map()
  );
  // Kunci efek berupa STRING, bukan array: `carts` berganti identitas pada
  // tiap ketikan di kolom catatan, jadi memakainya langsung sebagai dependency
  // membuat RPC menembak ulang per ketikan. Stringnya yang stabil.
  const limitedProductKey = useMemo(
    () =>
      carts
        .filter((c) => c.poLimited)
        .map((c) => c.productId)
        .sort()
        .join(","),
    [carts]
  );
  useEffect(() => {
    const ids = limitedProductKey ? limitedProductKey.split(",") : [];
    if (ids.length === 0) {
      setQuotaByVariant(new Map());
      return;
    }
    let alive = true;
    Promise.all(ids.map((id) => fetchPreorderQuotaRows(id))).then((all) => {
      if (!alive) return;
      const next: QuotaByVariant = new Map();
      for (const rows of all) {
        for (const row of rows) {
          next.set(row.variantId, { quota: row.quota, used: row.used });
        }
      }
      setQuotaByVariant(next);
    });
    return () => {
      alive = false;
    };
  }, [limitedProductKey]);

  // Baris yang qty-nya sudah melewati sisa. Gagal memuat peta → kosong →
  // checkout berjalan seperti sebelumnya (gerbang otoritatif tetap trigger DB).
  const overQuota = useMemo(
    () =>
      carts.flatMap((c) =>
        overQuotaCartLines(c.lines, quotaByVariant).map((x) => ({
          ...x,
          productName: c.productName,
        }))
      ),
    [carts, quotaByVariant]
  );
  const overQuotaMessage =
    overQuota.length === 0
      ? ""
      : overQuota
          .map(
            (x) =>
              `"${x.line.variationName}" tinggal ${x.remaining} pcs (di keranjang ${x.line.qty})`
          )
          .join("; ");

  // DP via NOTA (mig 072): tidak ada lagi upload bukti DP saat checkout —
  // nota DP otomatis terbit dan dibayar dari menu Tagihan.
  const canSubmit = Boolean(
    cart &&
      customerName.trim() &&
      phone.trim() &&
      address.trim() &&
      overQuota.length === 0
  );
  const submitOrder = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // bughunt H1-1: guard sinkron duluan — form ini bisa disubmit lewat Enter
    // di kolom manapun, jalur terpisah dari klik tombol CHECKOUT, dan tidak ada
    // RPC/idempotency key di baliknya (createCheckoutOrder = tulisan langsung).
    if (submitLockRef.current) return;
    // Safety-net: tamu yang mencoba submit diarahkan ke halaman masuk.
    if (!customer) {
      router.push(loginHref);
      return;
    }
    if (!cart || !canSubmit) return;
    if (!canOrder) {
      setMessage("PO untuk produk ini sudah ditutup. Pesanan tidak dapat dibuat.");
      return;
    }
    if (overQuota.length > 0) {
      setMessage(`Kuota PO sudah tidak cukup: ${overQuotaMessage}. Kurangi jumlahnya atau hapus varian itu.`);
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);
    setMessage("");

    try {
      const remoteOrderCode = await createCheckoutOrder({
        cart,
        customerName,
        phone,
        address,
        note,
        subtotal,
        paymentProof
      });
      // Gabungan hadiah lintas pesanan (mig 8880, H54): baris jalur ini ditulis
      // langsung, jadi server diminta menilainya bersama sisa pesanan
      // sebelumnya. Best-effort — pesanannya sudah jadi.
      if (remoteOrderCode) await attachMyCheckoutGifts().catch(() => undefined);

      // Hindari dual-source: simpan ke localStorage HANYA untuk guest
      // (belum login) atau saat Supabase tak tersedia (remoteOrderCode null).
      // User login → Supabase jadi sumber tunggal, riwayat dibaca dari DB.
      let localOrderId: string | null = null;
      if (!customer || !remoteOrderCode) {
        localOrderId = saveLocalOrder({
          productId: cart.productId,
          productSlug: cart.productSlug,
          productName: cart.productName,
          productImage: cart.productImage,
          productStatus: cart.preorderStatus,
          customerName,
          phone,
          address,
          note,
          estimatedReady: cart.estimatedReady ?? "sesuai update admin",
          totalQty,
          totalPrice: subtotal,
          lines: cart.lines
        }).id;
      }

      // Pixel iklan: jalur form lama juga sukses checkout — Purchase sebelum
      // clearCart, HANYA bila order benar-benar tercipta di DB (remoteOrderCode
      // null = tersimpan lokal saja, bukan konversi). Subtotal dari keranjang
      // yang di-checkout saja.
      if (remoteOrderCode) {
        firePixelEvent(
          "Purchase",
          buildPurchasePayload({
            cart,
            subtotal: cartSubtotal([cart]),
            orderCode: remoteOrderCode,
          })
        );
        // Atribusi iklan (mig 4170) — first-touch → Laporan Iklan.
        recordOrderAttribution(remoteOrderCode);
      }

      setCreatedOrderId(remoteOrderCode ?? localOrderId ?? "Pesanan tersimpan");
      setCustomerName("");
      setPhone("");
      setAddress("");
      setNote("");
      setPaymentProof(null);
      setShowCheckoutForm(false);
      clearCart();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Checkout gagal.");
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  /**
   * Harga satuan AKHIR komponen bundel untuk pixel iklan — harga terkini
   * (jawaban terakhir bila isi bundel baru saja berubah jumlahnya), sesudah
   * voucher bila `previewLines` diberi. Tak termuat → null (payload tanpa nilai).
   */
  const bundleUnitPriceOf = (previewLines: Parameters<typeof bundleFinalUnitPrices>[1]) => {
    const entries =
      bundlePricing.entries ??
      bundles.flatMap((b) => {
        const state = bundlePricing.lastByKey?.get(b.key);
        return state?.status === "ok" ? [state.entry] : [];
      });
    const finals = bundleFinalUnitPrices(entries, previewLines);
    return (key: string, componentId: string): number | null => {
      const line = entries.find((e) => e.cartKey === key)?.lines.find((l) => l.componentId === componentId);
      return line ? finals.get(line.key) ?? null : null;
    };
  };

  /**
   * Checkout keranjang yang memuat ≥ 1 BUNDEL (mig 8040): SATU RPC untuk
   * seluruh keranjang — baris biasa, komponen bundel, dan voucher bila dipilih
   * (K5). Hasil:
   *   • `done`     — pesanan tercipta, keranjang dibersihkan, pindah halaman;
   *   • `stop`     — bundel diubah jadi baris biasa, ATAU harga bundel naik
   *                  dari yang dipajang kartu (BUNDLE_PRICE_CHANGED); pembeli
   *                  diminta memeriksa total baru sebelum menekan lagi;
   *   • `fallback` — tak satu bundel pun lebih hemat (pembeli sudah melihatnya
   *                  di kartu): bundel sudah diubah jadi baris biasa, lanjut
   *                  jalur lama dengan `carts` ini.
   */
  const runBundleCheckout = async (
    buyerId: string,
    gifts: typeof cartGifts.payload
  ): Promise<{ kind: "done" } | { kind: "stop" } | { kind: "fallback"; carts: CartState[] }> => {
    // Kunci yang masih tersimpan = percobaan sebelumnya gagal dengan hasil TAK
    // DIKETAHUI (mis. respons hilang padahal RPC sudah commit). Percobaan ini
    // memeriksa replay-nya lebih dulu, SEBELUM gerbang klien mana pun — kunci
    // baru di sini berarti pesanan ganda.
    const keyOutstanding = bundleRequestIdRef.current !== null;
    bundleRequestIdRef.current ??= crypto.randomUUID();
    let result: BundleCheckoutResult;
    try {
      result = await createCheckoutOrdersWithBundles({
        carts,
        bundles,
        customerId: buyerId,
        voucherId: voucherSelection?.voucherId ?? null,
        voucherCode: voucherSelection?.code ?? null,
        requestId: bundleRequestIdRef.current,
        // Harga per bundel yang dipajang kartu (harga terkini; potret bila
        // belum termuat) — naik di checkout = berhenti & konfirmasi (§6.4).
        expected: bundleExpectedTotals(bundles, bundlePricing.lastByKey),
        keyOutstanding,
        gifts
      });
    } catch (error) {
      const raw = error instanceof Error ? error.message : "";
      // Kunci dilepas HANYA bila terbukti belum tercatat di server
      // (`bundleCheckoutKeyReleasable`); galat jaringan / tak dikenal
      // menyimpannya supaya percobaan berikutnya memeriksa replay dulu.
      if (bundleCheckoutKeyReleasable(raw)) bundleRequestIdRef.current = null;
      if (/GIFT_INVALID:/.test(raw)) {
        // Hadiah gugur di server (berakhir, stok habis, hak berubah) — belum ada
        // yang ditulis. Aturan yang disebut galat dilepas bila pembeli memilih
        // "Lanjut tanpa hadiah"; tanpa nama = semua hadiah dilepas.
        const name = raw.match(/GIFT_INVALID:[a-z_]+\|(.*)$/)?.[1]?.trim() ?? "";
        const ruleIds = name
          ? cartGifts.entries.filter((e) => e.rule.name.trim() === name).map((e) => e.rule.id)
          : [];
        cartGifts.reload();
        setGiftProblem({
          text: giftErrorMessage(raw) ?? "Hadiah di keranjang berubah. Periksa hadiahnya, lalu pesan lagi.",
          ruleIds
        });
        return { kind: "stop" };
      }
      if (raw === "BUNDLE_PRICE_CHANGED") {
        // §6.4 "harga berubah → konfirmasi": belum ada yang ditulis ke server
        // (kunci tertinggal sudah diperiksa replay-nya sebelum gerbang ini).
        // Harga dimuat ulang (tombol ditahan `bundlePending` sampai angka baru
        // tampil di kartu), pembeli memeriksa lalu menekan lagi.
        bundlePricing.reload();
        setMessage(
          bundleErrorMessage(raw) ?? "Harga bundling berubah — periksa total terbaru, lalu tekan Keep Produk lagi."
        );
        return { kind: "stop" };
      }
      if (raw === "BUNDLE_NONE_APPLIES") {
        // Aturan termurah (§4.2): bundel yang tak lebih hemat dipesan sebagai
        // baris biasa TANPA jejak bundel. Belum ada yang ditulis ke server
        // (replay kunci tertinggal sudah diperiksa lebih dulu).
        const informed = bundlesKnownNotApplying(bundles, bundlePricing.lastByKey);
        // Harga terpisah terkini per komponen — tampilan baris baru di
        // keranjang (checkout membaca ulang harga dari DB).
        let live: ReadonlyMap<string, BundleLiveState<ResolvedBundle>> | null = bundlePricing.lastByKey;
        if (!live || bundles.some((b) => live?.get(b.key)?.status !== "ok")) {
          const fresh = await resolveBundlesPricing(bundles, buyerId).catch(() => null);
          live = fresh ? new Map(fresh.map((e) => [e.cartKey, { status: "ok", entry: e }] as const)) : live;
        }
        const converted = bundlesAsCartLines(carts, bundles, (key, componentId) => {
          const state = live?.get(key);
          if (state?.status !== "ok") return undefined;
          const line = state.entry.lines.find((l) => l.componentId === componentId);
          // Harga terpisah BESERTA tambahannya (D14-31, bughunt D15 T6).
          return line ? { price: line.baseUnitPrice, priceExtra: line.priceExtra } : undefined;
        });
        replaceCarts(converted);
        clearBundles();
        const notice = bundleErrorMessage(raw) ?? "";
        if (!informed) {
          setMessage(`${notice} Periksa total terbaru, lalu tekan Keep Produk lagi.`);
          return { kind: "stop" };
        }
        setMessage(notice);
        return { kind: "fallback", carts: converted };
      }
      if (/VOUCHER_INVALID:/.test(raw)) {
        // Idiom jalur voucher: voucher gugur → lepas pilihan, bangun ulang
        // blok voucher, tahan tombol di batch yang sama (D11-2).
        setVoucherSelection(null);
        setVoucherSectionKey((k) => k + 1);
        setVoucherPending(true);
      }
      // Harga/ketersediaan bundel dimuat ulang supaya kartu memajang sebabnya.
      bundlePricing.reload();
      throw new Error(
        bundleErrorMessage(raw) ??
          voucherErrorMessage(raw, friendlyOrderItemInsertError(raw) || "Gagal membuat pesanan.")
      );
    }
    bundleRequestIdRef.current = null;
    // Pixel: Purchase per PESANAN (satu per produk) dengan harga AKHIR —
    // baris biasa sesudah voucher (pratinjau server), komponen bundel = harga
    // bundel (sesudah voucher bila ada). Harga belum termuat → tanpa nilai.
    const previewLines = voucherSelection?.preview.lines ?? null;
    const pixelCarts = bundleCheckoutPixelCarts(
      cartsAsPixelCarts(carts, previewLines),
      bundles,
      bundleUnitPriceOf(previewLines)
    );
    // Tepat SATU Purchase + atribusi per order_code (pesanan PO yang digabung
    // memulangkan kode lamanya; baris biasa & bundel satu produk = satu
    // pesanan = satu keranjang pixel, tak dobel).
    const fired = new Set<string>();
    // Pesanan yang isinya HANYA hadiah (Rp 0) bukan konversi — tanpa Purchase
    // & tanpa atribusi (rencana §4.4).
    const giftOnly = gifts.length
      ? giftOnlyProductIds(cartGifts.entries, new Set([...carts.map((c) => c.productId), ...bundleProductIds]))
      : new Set<string>();
    for (const ok of result.orders) {
      if (fired.has(ok.orderCode)) continue;
      fired.add(ok.orderCode);
      const pixelCart = pixelCarts.find((c) => c.productId === ok.productId);
      if (!pixelCart && giftOnly.has(ok.productId)) continue;
      if (pixelCart) {
        firePixelEvent(
          "Purchase",
          buildPurchasePayload({
            cart: pixelCart,
            subtotal: pixelCartSubtotal(pixelCart),
            orderCode: ok.orderCode,
          })
        );
      }
      recordOrderAttribution(ok.orderCode);
    }
    const landingSub = cartLandingSubTabWithBundles(carts, bundles);
    // Satu transaksi → semua yang dikirim sudah jadi pesanan.
    for (const ok of result.orders) clearProduct(ok.productId);
    clearBundles();
    if (voucherSelection) {
      clearCartVoucherPick();
      setVoucherSelection(null);
    }
    router.push(`/orders?tab=keranjang&sub=${landingSub}`);
    return { kind: "done" };
  };

  // "Keep Produk" (revisi 2026-08): langsung membuat pesanan TANPA
  // popup konfirmasi — keranjang → pesanan terasa dua langkah kembar dan popup
  // hanya mengulang rincian yang sudah tampil di kartu keranjang. Pesanan pakai
  // data customer yang login (alamat dikumpulkan saat request kirim), lalu
  // pembeli dibawa ke halaman Pesanan pada sub-kolom tempat barangnya mendarat
  // (ready_stock → Sudah Ready, PO → Belum Ready).
  const submitToOrders = async (opts: { gifts?: typeof cartGifts.payload } = {}) => {
    // bughunt H1-1: guard sinkron duluan, sebelum apa pun lain (lihat catatan
    // di submitOrder — tombol ini juga tanpa RPC/idempotency key di baliknya).
    if (submitLockRef.current) return;
    if (carts.length === 0 && !hasBundles) return;
    setMessage("");
    setGiftProblem(null);
    // Hadiah yang dibawa checkout ini: pilihan kartu, atau muatan yang sudah
    // dikurangi "Lanjut tanpa hadiah".
    const gifts = opts.gifts ?? cartGifts.payload;
    if (!customer) {
      router.push(loginHref);
      return;
    }
    if (voucherPending) {
      setMessage("Voucher masih diperiksa. Tunggu angkanya muncul sebelum memesan.");
      return;
    }
    if (bundlePending) {
      setMessage("Harga bundling masih dimuat. Tunggu angkanya muncul sebelum memesan.");
      return;
    }
    if (cartGifts.pending && !opts.gifts) {
      setMessage("Hadiah pembelian masih dimuat. Tunggu sebentar sebelum memesan.");
      return;
    }
    if (!canOrder) {
      setMessage("PO untuk produk ini sudah ditutup. Pesanan tidak dapat dibuat.");
      return;
    }
    if (overQuota.length > 0) {
      setMessage(`Kuota PO sudah tidak cukup: ${overQuotaMessage}. Kurangi jumlahnya atau hapus varian itu.`);
      return;
    }
    if (bundleBlocked) {
      setMessage("Ada bundling yang tidak tersedia. Hapus bundlingnya (atau tekan Coba lagi), lalu pesan lagi.");
      return;
    }
    // Pixel iklan: InitiateCheckout tepat sebelum submit langsung (PR #719
    // menghapus popup konfirmasi — semua gerbang di atas sudah lolos).
    // Null ("harga menyusul") → payload tanpa value.
    firePixelEvent(
      "InitiateCheckout",
      hasBundles
        ? buildCheckoutPayload(
            bundleCheckoutPixelCarts(cartsAsPixelCarts(carts, null), bundles, bundleUnitPriceOf(null)),
            cartGrandTotal(subtotal, carts.length, bundlesTotal)
          )
        : buildCheckoutPayload(carts, subtotal)
    );
    submitLockRef.current = true;
    setIsSubmitting(true);

    try {
      // Keranjang yang dikirim jalur lama. Hanya berbeda dari `carts` saat
      // cadangan bundel (BUNDLE_NONE_APPLIES) mengubah bundel jadi baris biasa.
      let checkoutCarts = carts;
      if (hasBundles || gifts.length > 0) {
        const outcome = await runBundleCheckout(customer.id, gifts);
        if (outcome.kind === "done") return;
        if (outcome.kind === "stop") {
          submitLockRef.current = false;
          setIsSubmitting(false);
          return;
        }
        checkoutCarts = outcome.carts;
      }
      if (voucherSelection) {
        // Checkout BER-VOUCHER: satu RPC untuk semua produk yang dirender —
        // server menghitung ulang harga & menulis order_items sendiri
        // (keputusan pemilik Q2). Gagal satu produk = seluruhnya batal.
        voucherRequestIdRef.current ??= crypto.randomUUID();
        let result;
        try {
          result = await createCheckoutOrdersWithVoucher({
            carts: checkoutCarts,
            customerId: customer.id,
            voucherId: voucherSelection.voucherId,
            // Voucher ber-kode (mig 6990): kode ikut checkout ini saja.
            voucherCode: voucherSelection.code,
            requestId: voucherRequestIdRef.current
          });
        } catch (error) {
          const raw = error instanceof Error ? error.message : "";
          if (/VOUCHER_INVALID:/.test(raw)) {
            // Voucher gugur di server (kuota habis / periode lewat / harga
            // berubah) — lepas pilihan, kunci idempoten dibuang, pembeli
            // memilih ulang; keranjang tetap utuh.
            voucherRequestIdRef.current = null;
            setVoucherSelection(null);
            setVoucherSectionKey((k) => k + 1);
            // Tombol ditahan DI BATCH YANG SAMA dengan remount: blok baru baru
            // melapor `pending` di efeknya, dan tanpa ini ada jeda beberapa
            // milidetik ketika ketukan cepat memesan harga penuh (D11-2).
            setVoucherPending(true);
          }
          // Galat dari trigger di dalam RPC (mis. `PROMO_QUOTA:*`, mig 6020)
          // bukan kegagalan voucher — ia lewat tanpa dipetakan lalu tampil
          // sebagai kode mentah. Dialirkan ke penerjemah insert order_items
          // dulu; yang tak dikenalinya kembali apa adanya seperti dulu.
          throw new Error(
            voucherErrorMessage(raw, friendlyOrderItemInsertError(raw) || "Gagal membuat pesanan.")
          );
        }
        voucherRequestIdRef.current = null;
        // Kunci pratinjau mengikuti keranjang yang dipratinjau; keranjang hasil
        // cadangan bundel berbeda urutannya → nilai Purchase dari harga keranjang.
        const previewForPixel = checkoutCarts === carts ? voucherSelection.preview.lines : [];
        for (const ok of result.orders) {
          const orderedCart = checkoutCarts.find((c) => c.productId === ok.productId);
          if (orderedCart) {
            // Nilai Purchase = harga AKHIR (setelah voucher) baris produk ini,
            // dari pratinjau server yang sama dengan yang ditulis RPC.
            const ci = checkoutCarts.indexOf(orderedCart) + 1;
            const paid = previewForPixel
              .filter((l) => l.key.startsWith(`${ci}:`) && l.unitPrice !== null)
              .reduce((sum, l) => {
                const line = orderedCart.lines[Number(l.key.split(":")[1]) - 1];
                return sum + (l.unitPrice as number) * (line?.qty ?? 0);
              }, 0);
            firePixelEvent(
              "Purchase",
              buildPurchasePayload({
                cart: orderedCart,
                subtotal: paid > 0 ? paid : cartSubtotal([orderedCart]),
                orderCode: ok.orderCode,
              })
            );
          }
          recordOrderAttribution(ok.orderCode);
        }
        const landingSub = cartLandingSubTab(checkoutCarts);
        for (const ok of result.orders) clearProduct(ok.productId);
        // Pilihan voucher sudah TERPAKAI: buang kuncinya di localStorage supaya
        // checkout berikutnya tidak membawa voucher lama tanpa dipilih.
        clearCartVoucherPick();
        setVoucherSelection(null);
        router.push(`/orders?tab=keranjang&sub=${landingSub}`);
        return;
      }

      // Satu order TERPISAH per produk (best-effort).
      const result = await createCheckoutOrders({
        carts: checkoutCarts,
        customerName: customer.fullName,
        phone: customer.phone,
        address: "",
        paymentProof: null
      });
      // Gabungan hadiah lintas pesanan (mig 8880, H54): baris jalur ini ditulis
      // langsung, jadi server diminta menilainya bersama sisa pesanan
      // sebelumnya — hadiah "ditambahkan otomatis" di kartu. Best-effort.
      if (result.succeeded.length > 0) await attachMyCheckoutGifts().catch(() => undefined);
      // Pixel iklan: Purchase per ORDER yang sukses (multi-produk = satu
      // order per produk, masing-masing ber-order_code sendiri sebagai
      // event_id dedup). Ditembak SEBELUM clearProduct — sesudahnya baris
      // keranjangnya hilang — dan sebelum cabang partial-success di bawah
      // yang `return` lebih dulu: order yang sukses di batch gagal-sebagian
      // tetap ter-track.
      for (const ok of result.succeeded) {
        const orderedCart = checkoutCarts.find((c) => c.productId === ok.productId);
        if (orderedCart) {
          firePixelEvent(
            "Purchase",
            buildPurchasePayload({
              cart: orderedCart,
              subtotal: cartSubtotal([orderedCart]),
              orderCode: ok.orderCode,
            })
          );
        }
        // Atribusi iklan (mig 4170) — first-touch → Laporan Iklan.
        recordOrderAttribution(ok.orderCode);
      }
      // Bersihkan produk yang sukses; sisakan yang gagal agar bisa di-retry.
      for (const ok of result.succeeded) clearProduct(ok.productId);
      if (result.failed.length > 0) {
        // Kode pesanan yang SUDAH jadi disebut + "tidak perlu dipesan ulang"
        // (bughunt ketiga): kalimat lama "N produk masuk" membuat pembeli
        // menambahkan ulang semuanya → pesanan dobel.
        setMessage(partialCheckoutMessage(result.succeeded, result.failed));
        submitLockRef.current = false;
        setIsSubmitting(false);
        return;
      }
      router.push(`/orders?tab=keranjang&sub=${cartLandingSubTab(checkoutCarts)}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Gagal membuat pesanan.");
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ─── Success state ─────────────────────────────────────────────────────────

  if (createdOrderId) {
    return (
      <div
        className={
          isInline
            ? "rounded-2xl border border-neutral-100 bg-white p-5 text-center shadow-sm"
            : "min-h-screen bg-neutral-100 px-4 py-6"
        }
      >
        <div
          className={
            isInline
              ? "flex flex-col items-center"
              : "flex min-h-[70vh] flex-col items-center justify-center text-center"
          }
        >
          <CheckCircle2 className="text-leaf" size={52} />
          <h1 className="mt-4 text-xl font-extrabold text-ink">Preorder masuk!</h1>
          <p className="mt-1 text-sm font-semibold text-muted">{createdOrderId}</p>
          <Link
            href="/orders"
            className="mt-6 flex h-12 w-full items-center justify-center rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft"
          >
            Lihat Pesanan
          </Link>
          <Link
            href="/"
            className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-neutral-100 bg-white text-sm font-extrabold text-ink"
          >
            Belanja Lagi
          </Link>
        </div>
      </div>
    );
  }

  // ─── Page mode ────────────────────────────────────────────────────────────

  if (!isInline) {
    return (
      <div className="min-h-screen bg-neutral-100">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-neutral-100 bg-shell/95 px-4 py-3 backdrop-blur safe-top">
          <Link
            href="/"
            aria-label="Kembali"
            className="grid h-10 w-10 place-items-center rounded-full bg-white text-ink shadow-sm"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-lg font-extrabold text-ink">Checkout</h1>
            <p className="text-xs font-medium text-muted">
              Beberapa produk dalam satu keranjang
            </p>
          </div>
        </header>

        {!cart ? (
          <section className="flex min-h-[65vh] flex-col items-center justify-center px-4 text-center">
            <div className="grid h-20 w-20 place-items-center rounded-full bg-white text-shopee shadow-sm">
              <ShoppingCart size={32} />
            </div>
            <h2 className="mt-4 text-lg font-extrabold text-ink">Keranjang kosong</h2>
            <Link
              href="/"
              className="mt-5 flex h-12 w-full items-center justify-center rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft"
            >
              Pilih Produk
            </Link>
          </section>
        ) : (
          <form onSubmit={submitOrder} className="space-y-3 px-4 py-4">
            <CartItemsSection
              cart={cart}
              subtotal={subtotal}
              updateLine={updateLine}
              updateLineNote={updateLineNote}
              removeLine={removeLine}
              quotaByVariant={quotaByVariant}
            />
            <CustomerFormSection
              customerName={customerName}
              phone={phone}
              address={address}
              note={note}
              paymentProof={paymentProof}
              onNameChange={setCustomerName}
              onPhoneChange={setPhone}
              onAddressChange={setAddress}
              onNoteChange={setNote}
              onProofChange={setPaymentProof}
            />
            {message ? (
              <p className="rounded-xl bg-white p-3 text-sm font-bold text-shopee shadow-sm">
                {message}
              </p>
            ) : null}
            <div className="sticky bottom-[73px] z-20 border-t border-neutral-100 bg-shell pb-3 pt-2">
              {needsLogin ? (
                <Link
                  href={loginHref}
                  className="flex h-12 w-full items-center justify-center rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99]"
                >
                  Masuk untuk Membuat Pesanan
                </Link>
              ) : notReseller ? (
                <NotResellerNotice />
              ) : !canOrder ? (
                <POClosedNotice />
              ) : (
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="h-12 w-full rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99] disabled:bg-zinc-300"
                >
                  {isSubmitting ? "MEMPROSES…" : "CHECKOUT"}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    );
  }

  // ─── Inline mode: keranjang kosong ───────────────────────────────────────

  if (!cart && !hasBundles) {
    return (
      <section className="rounded-2xl border border-neutral-100 bg-white p-4 shadow-sm">
        <KeranjangHeader count={0} />
        <div className="flex flex-col items-center py-7 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-shopeeSoft/60 text-shopee">
            <ShoppingCart size={28} />
          </div>
          <p className="mt-3 text-sm font-extrabold text-ink">Keranjang Kosong</p>
          <p className="mt-1 text-xs font-medium text-muted">
            Pilih variasi dan klik "Tambah ke Keranjang"
          </p>
        </div>
        <button
          type="button"
          disabled
          className="h-12 w-full rounded-xl bg-zinc-100 text-sm font-extrabold text-zinc-400"
        >
          Keep Produk
        </button>
      </section>
    );
  }

  // ─── Inline mode: ada item di keranjang ───────────────────────────────────

  // Hadiah pembelian menempel di bawah baris varian syaratnya (putusan pemilik
  // 2026-10-28, RENCANA-hadiah-pembelian §15.8): baris syarat terakhir di kartu
  // produk pertama yang memuat syaratnya. Yang tak menemukan barisnya tetap
  // jadi kartu sendiri di bawah daftar.
  const giftPlacement = giftEntryHosts(
    carts.map((c) => ({
      productId: c.productId,
      lines: c.lines.map((l) => ({ productId: c.productId, variationId: l.variationId }))
    })),
    cartGifts.entries
  );
  const giftCard = (entry: (typeof cartGifts.entries)[number], nested: boolean) => (
    <GiftCartCard
      key={entry.rule.id}
      entry={entry}
      nested={nested}
      onClaimChange={(lines) => cartGifts.setClaim(entry.rule.id, lines)}
    />
  );

  return (
    <section className="rounded-2xl border border-neutral-100 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
        <KeranjangHeader count={totalQty} />
      </div>

      {/* Items — satu grup per produk */}
      <div className="space-y-3 px-4 py-3">
        {carts.map((c) => (
          <div
            key={c.productId}
            className={
              carts.length + bundles.length > 1
                ? "rounded-xl border border-neutral-100 p-3"
                : ""
            }
          >
            {carts.length + bundles.length > 1 ? (
              <div className="mb-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => clearProduct(c.productId)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-muted"
                >
                  <Trash2 size={11} />
                  Hapus produk
                </button>
              </div>
            ) : null}
            <CartItemsSection
              cart={c}
              subtotal={cartSubtotal([c])}
              updateLine={updateLine}
              updateLineNote={updateLineNote}
              removeLine={removeLine}
              quotaByVariant={quotaByVariant}
              hasVoucher={voucherCovered.has(c.productId)}
              bundleDpLines={bundleDpLines?.get(c.productId)}
              afterLine={(variationId) => {
                const entries = giftPlacement.after.get(
                  giftLineKeyOf({ productId: c.productId, variationId })
                );
                return entries?.length ? entries.map((entry) => giftCard(entry, true)) : null;
              }}
            />
          </div>
        ))}

        {/* Bundel (mig 8040) — kartu kelompok sesudah produk biasa; isinya
            tak pernah melebur dengan baris produk yang variannya sama. */}
        {bundles.map((b) => (
          <BundleCartCard
            key={b.key}
            bundle={b}
            live={bundlePricing.lastByKey?.get(b.key)}
            refreshing={Boolean(customer) && !bundlePricing.ready}
            onQtyChange={(qty) => updateBundleQty(b.key, qty)}
            onRemove={() => removeBundle(b.key)}
            onRetry={bundlePricing.reload}
          />
        ))}

        {/* Hadiah pembelian (mig 8680) yang baris syaratnya tak tampil di
            daftar produk — kartu sendiri sesudah bundel. Selebihnya menempel
            di bawah baris varian syaratnya (§15.8). */}
        {giftPlacement.unplaced.map((entry) => giftCard(entry, false))}
        {cartGifts.failed ? (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-shell p-3">
            <p className="text-[11px] font-semibold text-muted">Hadiah pembelian belum bisa dimuat.</p>
            <button
              type="button"
              onClick={cartGifts.reload}
              className="shrink-0 text-[11px] font-extrabold text-shopee"
            >
              Coba lagi
            </button>
          </div>
        ) : null}

        {/* Voucher produk (mig 5610) — di bawah daftar produk, hanya pembeli
            login; tak merender apa pun bila toko tak punya voucher yang
            memenuhi syarat. */}
        {customer && canOrder ? (
          <CartVoucherSection
            key={voucherSectionKey}
            carts={carts}
            customer={customer}
            onSelectionChange={handleVoucherSelection}
            onSummaryChange={handleVoucherSummary}
            onCoverageChange={handleVoucherCoverage}
            onPendingChange={handleVoucherPending}
            bundleRows={hasBundles ? (bundlePricing.ready ? bundlePricing.entries : null) : undefined}
            bundleSignature={bundlePricing.signature}
            bundleProductIds={hasBundles ? bundleProductIds : undefined}
          />
        ) : null}

        {hasBundles && carts.length + bundles.length > 1 ? (
          <BundleGrandTotalRow total={cartGrandTotal(subtotal, carts.length, bundlesTotal)} />
        ) : null}

        {!hasBundles && carts.length > 1 ? (
          <div className="flex items-center justify-between border-t border-neutral-100 pt-3">
            <span className="text-sm font-extrabold text-ink">
              Total semua produk
            </span>
            <span
              className={`text-base font-extrabold ${
                subtotal !== null ? "text-shopee" : "text-muted"
              }`}
            >
              {subtotal !== null ? formatPrice(subtotal) : "Harga menyusul"}
            </span>
          </div>
        ) : null}
      </div>

      {/* Keep Produk — tanpa popup konfirmasi (wajib login) */}
      <div className="border-t border-neutral-100 px-4 pb-4 pt-3">
        {needsLogin ? (
          <Link
            href={loginHref}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99]"
          >
            Masuk untuk Pesan
          </Link>
        ) : notReseller ? (
          <NotResellerNotice />
        ) : !canOrder ? (
          <POClosedNotice />
        ) : (
          <>
            {/* Kuota PO tidak cukup (mig 3970) — ditahan DI SINI, bukan dibiarkan
                ditolak trigger DB setelah pembeli menekan tombol. */}
            {overQuota.length > 0 ? (
              <p className="mb-2 text-xs font-bold text-shopee">
                Kuota PO sudah tidak cukup: {overQuotaMessage}.
              </p>
            ) : null}
            {bundleBlocked ? (
              <p className="mb-2 text-xs font-bold text-shopee">
                Ada bundling yang tidak tersedia — hapus bundlingnya untuk melanjutkan.
              </p>
            ) : null}
            {/* Ringkasan Harga Umum · Voucher · Diskon Level · Total diulang
                tepat di atas tombol (keputusan Q12) — hanya bila blok voucher
                relevan untuk keranjang ini. */}
            {voucherSummary ? (
              <div className="mb-2">
                <CartVoucherSummaryCard summary={voucherSummary} />
              </div>
            ) : null}
            {/* Popup konfirmasi dihapus → error checkout (mis. PO keburu
                ditutup) kini tampil di sini. */}
            {message ? (
              <p className="mb-2 rounded-lg bg-shell p-2.5 text-xs font-bold text-shopee">
                {message}
              </p>
            ) : null}
            {/* Hadiah gugur saat checkout (H14): tak pernah turun diam-diam. */}
            {giftProblem ? (
              <div className="mb-2 rounded-lg bg-shell p-2.5">
                <p className="text-xs font-bold text-shopee">{giftProblem.text}</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                      // Nama aturan tak dikenali = semua hadiah dilepas. Hanya
                      // untuk kiriman ini (H40): kartunya tetap, tak "dilewati".
                      const drop = new Set(
                        giftProblem.ruleIds.length ? giftProblem.ruleIds : cartGifts.entries.map((e) => e.rule.id)
                      );
                      void submitToOrders({ gifts: cartGifts.payload.filter((g) => !drop.has(g.rule_id)) });
                    }}
                    className="h-10 rounded-lg bg-shopee text-xs font-extrabold text-white disabled:bg-zinc-300"
                  >
                    Lanjut tanpa hadiah
                  </button>
                  <button
                    type="button"
                    onClick={() => setGiftProblem(null)}
                    className="h-10 rounded-lg border border-neutral-200 bg-white text-xs font-extrabold text-ink"
                  >
                    Periksa hadiah
                  </button>
                </div>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => void submitToOrders()}
              disabled={overQuota.length > 0 || isSubmitting || voucherPending || bundleBlocked || bundlePending}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-shopee text-sm font-extrabold text-white shadow-soft transition active:scale-[0.99] disabled:bg-zinc-300"
            >
              <ShoppingCart size={17} />
              {isSubmitting
                ? "Memproses…"
                : voucherPending
                  ? "Memeriksa voucher…"
                  : bundlePending
                    ? "Memuat harga bundling…"
                    : "Keep Produk"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

// Ditampilkan saat pengguna SUDAH login tapi bukan reseller (mis. akun admin).
// Bukan kondisi "logout" — jadi jangan tampilkan tombol "Masuk".
function NotResellerNotice() {
  return (
    <div className="rounded-xl border border-neutral-100 bg-shell p-3 text-center">
      <p className="text-sm font-extrabold text-ink">Akun ini bukan reseller</p>
      <p className="mt-0.5 text-xs font-semibold text-muted">
        Pemesanan hanya untuk akun reseller. Buka panel admin untuk mengelola pesanan.
      </p>
      <Link
        href="/admin"
        className="mt-2 inline-flex h-10 items-center justify-center rounded-lg bg-shopee px-4 text-xs font-extrabold text-white shadow-soft"
      >
        Buka Panel Admin
      </Link>
    </div>
  );
}

// Ditampilkan saat produk di keranjang sudah TIDAK bisa dipesan lagi (mis. PO
// ditutup setelah item ditambahkan ke keranjang). Mencegah checkout keranjang basi.
function POClosedNotice() {
  return (
    <div className="rounded-xl border border-neutral-100 bg-shell p-3 text-center">
      <p className="text-sm font-extrabold text-ink">PO sudah ditutup</p>
      <p className="mt-0.5 text-xs font-semibold text-muted">
        Pesanan untuk produk ini tidak bisa dibuat lagi karena preorder telah
        ditutup. Hubungi admin bila ingin memesan.
      </p>
    </div>
  );
}

/** Total keranjang yang memuat bundel: baris biasa + Σ bundel (sebelum voucher). */
function BundleGrandTotalRow({ total }: { total: number | null }) {
  return (
    <div className="flex items-center justify-between border-t border-neutral-100 pt-3">
      <span className="text-sm font-extrabold text-ink">Total semua barang</span>
      <span className={`text-base font-extrabold ${total !== null ? "text-shopee" : "text-muted"}`}>
        {total !== null ? formatPrice(total) : "Harga menyusul"}
      </span>
    </div>
  );
}

function KeranjangHeader({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-2">
      <ShoppingCart size={17} className="shrink-0 text-shopee" />
      <h2 className="text-sm font-extrabold text-ink">Keranjang</h2>
      {count > 0 ? (
        <span className="rounded-full bg-shopee px-2 py-0.5 text-[10px] font-bold text-white">
          {count}
        </span>
      ) : null}
    </div>
  );
}

function CartItemsSection({
  cart,
  subtotal,
  updateLine,
  updateLineNote,
  removeLine,
  quotaByVariant,
  hasVoucher = false,
  bundleDpLines,
  afterLine
}: {
  cart: NonNullable<ReturnType<typeof useCart>["cart"]>;
  /**
   * Isi yang menempel tepat di bawah satu baris varian — kartu hadiah
   * pembelian yang barisnya jadi syarat (§15.8). Absen / null = tanpa apa pun.
   */
  afterLine?: (variationId: string) => ReactNode;
  /**
   * Baris komponen BUNDEL produk yang sama (mig 8040) — ikut DP pesanan PO
   * produk ini. Absen = DP persis seperti sebelumnya.
   */
  bundleDpLines?: ReadonlyArray<{ qty: number; unitPrice: number | null }>;
  /** Minimal satu voucher produk mencakup produk ini (mig 5660) — keterangan di bawah nama. */
  hasVoucher?: boolean;
  /** Sisa kuota per varian (mig 3970). Kosong = tak berkuota / belum termuat. */
  quotaByVariant: QuotaByVariant;
  subtotal: ReturnType<typeof useCart>["subtotal"];
  updateLine: ReturnType<typeof useCart>["updateLine"];
  updateLineNote: ReturnType<typeof useCart>["updateLineNote"];
  removeLine: ReturnType<typeof useCart>["removeLine"];
}) {
  return (
    <div className="space-y-3">
      {/* Product thumbnail + name */}
      <div className="flex gap-3">
        {/* PF-09: wrapper ber-dimensi tetap — StoreImage me-render null saat
            src kosong, tanpa wrapper kolom teks bergeser. */}
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-shopeeSoft">
          <StoreImage
            src={cart.productImage}
            alt={cart.productName}
            width={64}
            height={64}
            sizes="64px"
            className="h-16 w-16 object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-extrabold leading-5 text-ink">
            {cart.productName}
          </h3>
          <p className="mt-1 text-xs font-semibold text-muted">
            Ready {cart.estimatedReady ?? "sesuai update admin"}
          </p>
          {hasVoucher ? (
            <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-shopeeSoft/60 px-1.5 py-0.5 text-[10px] font-bold text-shopee">
              <TicketPercent size={11} />
              Terdapat voucher untuk produk ini
            </p>
          ) : null}
          <p className={`mt-1.5 text-base font-extrabold ${subtotal !== null ? "text-shopee" : "text-muted"}`}>
            {subtotal !== null ? formatPrice(subtotal) : "Harga menyusul"}
          </p>
        </div>
      </div>

      {/* Lines — tiap varian punya catatan sendiri */}
      <div className="space-y-3 border-t border-neutral-100 pt-3">
        {cart.lines.map((line) => {
          // Sisa kuota varian ini; `null` = tak berkuota / belum termuat →
          // stepper tak dibatasi, persis perilaku sebelum audit 2026-08-24.
          const remaining = cartLineRemaining(quotaByVariant.get(line.variationId));
          const over = remaining !== null && line.qty > remaining;
          const gifts = afterLine?.(line.variationId) ?? null;
          return (
          <div key={line.variationId} className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-ink">
                  {line.variationName}
                </p>
                <button
                  type="button"
                  onClick={() => removeLine(line.variationId, cart.productId)}
                  className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-muted"
                >
                  <Trash2 size={11} />
                  Hapus
                </button>
              </div>
              <QuantityStepper
                value={line.qty}
                max={remaining ?? undefined}
                onChange={(v) => updateLine(line.variationId, v, cart.productId)}
              />
            </div>
            {over ? (
              <p className="text-[11px] font-bold text-shopee">
                {remaining === 0
                  ? "Kuota PO varian ini sudah habis — hapus dari keranjang."
                  : `Kuota PO tinggal ${remaining} pcs — kurangi jumlahnya.`}
              </p>
            ) : null}
            <input
              value={line.note ?? ""}
              onChange={(e) =>
                updateLineNote(line.variationId, e.target.value, cart.productId)
              }
              placeholder="Catatan (Opsional, maks 20)"
              maxLength={20}
              className="h-9 w-full rounded-lg border border-neutral-100 bg-shell px-3 text-xs font-semibold text-ink outline-none transition focus:border-shopee"
            />
            {gifts ? <div className="ml-3 space-y-2 border-l-2 border-leaf/40 pl-2.5">{gifts}</div> : null}
          </div>
          );
        })}
      </div>

      {/* Subtotal */}
      <div className="flex items-center justify-between border-t border-neutral-100 pt-3">
        <span className="text-sm font-bold text-muted">Total harga</span>
        <span className={`text-base font-extrabold ${subtotal !== null ? "text-shopee" : "text-muted"}`}>
          {subtotal !== null ? formatPrice(subtotal) : "Harga menyusul"}
        </span>
      </div>
      {subtotal === null ? (
        <p className="text-[11px] font-semibold text-muted">
          Harga belum ditetapkan. Pesanan tetap bisa dibuat — harga dikonfirmasi admin setelah pesanan masuk.
        </p>
      ) : null}

      {/* DP wajib */}
      {cart.dpRequired ? (
        bundleDpLines?.length ? (
          <BundleAwareDpNotice cart={cart} bundleDpLines={bundleDpLines} />
        ) : (
          <div className="rounded-lg border border-marigold/30 bg-marigold/10 p-3">
            <p className="text-xs font-extrabold text-marigold">
              Wajib DP{cartDpTotal(cart) > 0 ? ` ${formatPrice(cartDpTotal(cart))}` : ""}
            </p>
            <p className="mt-0.5 text-[11px] font-semibold text-muted">
              Tagihan DP otomatis terbit setelah pesanan dibuat — bayar pakai saldo
              atau transfer dari menu Tagihan.
            </p>
          </div>
        )
      ) : null}
    </div>
  );
}

/**
 * DP produk yang juga dipesan di BUNDEL (mig 8040): pesanan PO produk ini
 * memuat baris biasa + baris bundelnya, jadi DP-nya menghitung keduanya. Harga
 * baris bundel belum diketahui pada DP persen → nominal tidak dipajang (angka
 * yang salah lebih buruk daripada tanpa angka).
 */
function BundleAwareDpNotice({
  cart,
  bundleDpLines
}: {
  cart: CartState;
  bundleDpLines: ReadonlyArray<{ qty: number; unitPrice: number | null }>;
}) {
  const dp = cartDpTotalWithExtraLines(cart, bundleDpLines);
  return (
    <div className="rounded-lg border border-marigold/30 bg-marigold/10 p-3">
      <p className="text-xs font-extrabold text-marigold">
        Wajib DP{dp !== null && dp > 0 ? ` ${formatPrice(dp)}` : ""}
      </p>
      <p className="mt-0.5 text-[11px] font-semibold text-muted">
        {dp !== null
          ? "Termasuk barang produk ini di bundling. "
          : "Nominalnya ikut barang produk ini di bundling — dihitung saat tagihan DP terbit. "}
        Tagihan DP otomatis terbit setelah pesanan dibuat — bayar pakai saldo atau
        transfer dari menu Tagihan.
      </p>
    </div>
  );
}

function CustomerFormSection({
  customerName,
  phone,
  address,
  note,
  paymentProof,
  onNameChange,
  onPhoneChange,
  onAddressChange,
  onNoteChange,
  onProofChange
}: {
  customerName: string;
  phone: string;
  address: string;
  note: string;
  paymentProof: File | null;
  onNameChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  onAddressChange: (v: string) => void;
  onNoteChange: (v: string) => void;
  onProofChange: (f: File | null) => void;
}) {
  return (
    <div className="space-y-2">
      <input
        value={customerName}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Nama"
        className="h-12 w-full rounded-lg border border-neutral-100 bg-shell px-3 text-sm font-semibold text-ink outline-none transition focus:border-shopee"
      />
      <input
        value={phone}
        onChange={(e) => onPhoneChange(e.target.value)}
        placeholder="Nomor WhatsApp"
        inputMode="tel"
        className="h-12 w-full rounded-lg border border-neutral-100 bg-shell px-3 text-sm font-semibold text-ink outline-none transition focus:border-shopee"
      />
      <textarea
        value={address}
        onChange={(e) => onAddressChange(e.target.value)}
        placeholder="Alamat"
        rows={3}
        className="w-full resize-none rounded-lg border border-neutral-100 bg-shell px-3 py-3 text-sm font-semibold text-ink outline-none transition focus:border-shopee"
      />
      <textarea
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder="Catatan checkout (Opsional)"
        rows={2}
        className="w-full resize-none rounded-lg border border-neutral-100 bg-shell px-3 py-3 text-sm font-semibold text-ink outline-none transition focus:border-shopee"
      />
      <label className="flex h-12 cursor-pointer items-center justify-center rounded-lg border border-neutral-100 bg-shell px-3 text-sm font-semibold text-muted">
        {paymentProof?.name ?? "Upload bukti transfer"}
        <input
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => onProofChange(e.target.files?.[0] ?? null)}
        />
      </label>
    </div>
  );
}
